import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { StatusBadge, PriorityBadge } from "@/components/StatusBadge";
import { fmtDate, fmtDateTime, fmtOrEmpty } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import {
  STAGES, STAGE_PRODUCTION, RO_CREATE_STAGE, FINAL_PHOTO_STAGE, PHOTO_CATEGORIES,
  CAN_EDIT_PROJECT, CAN_SET_PJ, CAN_STAGE_ADMIN, CAN_STAGE_PROD, CAN_STAGE_BACK, PJ_ROLES,
  ESTIMATE_VIEW, RAB_VIEW, HPP_VIEW, ESTIMATE_MANAGE, DEAL_VIEW, PROFIT_VIEW, RAP_VIEW,
} from "@/lib/constants";
import { DealCard } from "@/components/DealCard";
import { FinancialSummary } from "@/components/FinancialSummary";
import { RapPanel } from "@/components/RapPanel";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Pencil, ImagePlus, Trash2, Clock, FileText, UserPlus, ArrowRight, AlertTriangle, Printer } from "lucide-react";

function Row({ label, value }) {
  return (<div className="flex justify-between gap-4 py-1.5 border-b last:border-0 text-sm">
    <span className="text-muted-foreground shrink-0">{label}</span>
    <span className="font-medium text-right">{value || "-"}</span></div>);
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [history, setHistory] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photoCat, setPhotoCat] = useState(PHOTO_CATEGORIES[0]);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [note, setNote] = useState("");
  const [pjSel, setPjSel] = useState("");
  const [estList, setEstList] = useState([]);
  const [rabList, setRabList] = useState([]);
  const [hppSum, setHppSum] = useState(null);
  const canViewEst = user && ESTIMATE_VIEW.includes(user.role);
  const canViewRab = user && RAB_VIEW.includes(user.role);
  const canViewHpp = user && HPP_VIEW.includes(user.role);
  const canEdit = user && CAN_EDIT_PROJECT.includes(user.role);
  const canSetPj = user && CAN_SET_PJ.includes(user.role);
  const canViewDeal = user && DEAL_VIEW.includes(user.role);
  const canViewProfit = user && PROFIT_VIEW.includes(user.role);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setP(data);
      const h = await api.get(`/projects/${id}/history`);
      setHistory(h.data);
    } catch (err) {
      toast.error("Proyek tidak ditemukan"); navigate("/projects");
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canSetPj) api.get("/users").then(({ data }) => setStaff(data.filter((u) => u.active && PJ_ROLES.includes(u.role)))).catch(() => {}); }, [canSetPj]);
  useEffect(() => {
    if (!p) return;
    if (canViewEst) api.get("/estimates", { params: { project_uuid: id } }).then(({ data }) => setEstList(data)).catch(() => {});
    if (canViewRab) api.get("/rab", { params: { project_uuid: id } }).then(({ data }) => setRabList(data)).catch(() => {});
    if (canViewHpp && p.vehicle_id) api.get(`/hpp/${id}/${p.vehicle_id}`).then(({ data }) => setHppSum(data)).catch(() => {});
  }, [p, id, canViewEst, canViewRab, canViewHpp]);

  const flow = p?.stage_flow || STAGES;
  const curIdx = p ? flow.indexOf(p.stage) : -1;
  const nextStage = curIdx >= 0 ? flow[curIdx + 1] : null;
  const canViewRap = user && RAP_VIEW.includes(user.role);

  const canAdvanceTo = (stg) => {
    if (!user) return false;
    if (nextStage === "Closed" && !CAN_STAGE_BACK.includes(user.role)) return false;
    if (STAGE_PRODUCTION.includes(stg) && stg !== RO_CREATE_STAGE) return CAN_STAGE_PROD.includes(user.role);
    return CAN_STAGE_ADMIN.includes(user.role);
  };

  const advance = async () => {
    if (!nextStage) return;
    if (nextStage === RO_CREATE_STAGE && !p.penanggung_jawab_id) { toast.error("Tetapkan penanggung jawab dulu sebelum On Production."); return; }
    if (nextStage === RO_CREATE_STAGE && p.deal_status !== "Terkunci") { toast.error("Harga Deal belum dikunci Owner/Manager. SPK/RO belum dapat diterbitkan."); return; }
    if (nextStage === FINAL_PHOTO_STAGE && !(p.photos || []).some((ph) => (ph.category || "").toLowerCase().includes("hasil akhir"))) {
      toast.error("Unggah foto hasil akhir (kategori 'Foto Hasil Akhir') dulu di tab Photos."); return;
    }
    try {
      await api.patch(`/projects/${id}/stage`, { stage: nextStage, note });
      toast.success(`Tahap: ${nextStage}`); setNote(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const goBackStage = async (stg) => {
    const reason = window.prompt(`Alasan memundurkan tahap ke "${stg}" (wajib):`) || "";
    if (!reason) return;
    try {
      await api.patch(`/projects/${id}/stage`, { stage: stg, reason });
      toast.success(`Tahap dimundurkan ke ${stg}`); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const assignPj = async (uid) => {
    try { await api.patch(`/projects/${id}/pj`, { penanggung_jawab_id: uid }); toast.success("Penanggung jawab diperbarui"); setPjSel(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const uploadPhoto = async (files) => {
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData(); fd.append("file", file); fd.append("category", photoCat);
        await api.post(`/projects/${id}/photos`, fd);
      }
      toast.success("Foto ditambahkan"); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const deletePhoto = async (photoId) => {
    let reason = "";
    if (p.stage !== "Draft") { reason = window.prompt("Penghapusan foto proyek aktif memerlukan alasan (khusus Owner):") || ""; if (!reason) return; }
    else if (!window.confirm("Hapus foto ini?")) return;
    try { await api.delete(`/projects/${id}/photos/${photoId}`, { params: { reason } }); toast.success("Foto dihapus"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (loading || !p) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  const imgUrl = (path) => `${API}/files/${path}`;

  return (
    <div className="space-y-4" data-testid="project-detail-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)} data-testid="back-button"><ArrowLeft size={18} /></Button>
          <div>
            <div className="font-mono-k3 text-sm font-bold text-primary">{p.project_id}{p.ro_number && <span className="ml-2 text-emerald-700">{p.ro_number}</span>}</div>
            <h1 className="font-heading text-xl font-bold text-navy">{p.title}</h1>
            <div className="flex items-center gap-2 mt-1"><StatusBadge status={p.stage} /><PriorityBadge priority={p.priority} /></div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {p.ro_number && <Button variant="outline" onClick={() => navigate(`/projects/${id}/spk?v=internal`)} data-testid="spk-internal-btn"><Printer size={15} className="mr-1.5" /> SPK Produksi</Button>}
          {p.ro_number && canViewDeal && <Button variant="outline" onClick={() => navigate(`/projects/${id}/spk?v=customer`)} data-testid="spk-customer-btn"><Printer size={15} className="mr-1.5" /> SPK Customer</Button>}
          {canEdit && <Button onClick={() => navigate(`/projects/${id}/edit`)} data-testid="edit-project-btn" className="bg-primary hover:bg-red-700"><Pencil size={15} className="mr-1.5" /> Edit</Button>}
        </div>
      </div>

      {!p.ro_number && p.deal_status !== "Terkunci" && STAGES.indexOf(p.stage) < STAGES.indexOf(RO_CREATE_STAGE) && (
        <Card className="p-3 border-amber-300 bg-amber-50 flex items-center gap-2 text-sm text-amber-800" data-testid="deal-warning">
          <AlertTriangle size={16} /> Harga Deal {p.deal_status === "Belum Ada" ? "belum ditetapkan" : "belum dikunci"}. Wajib dikunci Owner/Manager sebelum On Production (SPK/RO terbit).
        </Card>
      )}

      {p.pj_missing && (
        <Card className="p-3 border-red-300 bg-red-50 flex items-center gap-2 flex-wrap" data-testid="pj-warning">
          <AlertTriangle size={16} className="text-primary" />
          <span className="text-sm font-medium text-primary flex-1">Belum ada penanggung jawab.</span>
          {canSetPj && (
            <Select value={pjSel} onValueChange={(v) => { setPjSel(v); assignPj(v); }}>
              <SelectTrigger className="w-[220px] bg-white" data-testid="assign-pj-select"><SelectValue placeholder="Tetapkan penanggung jawab" /></SelectTrigger>
              <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.role})</SelectItem>)}</SelectContent>
            </Select>
          )}
        </Card>
      )}

      {/* Stage control */}
      <Card className="p-4 space-y-2" data-testid="stage-control">
        <h3 className="font-heading font-semibold text-navy text-sm">Alur Tahap Proyek <span className="text-[11px] font-normal text-muted-foreground" data-testid="production-track">· Jalur {p.production_track}</span></h3>
        <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
          {flow.map((s, i) => (
            <span key={s} className={`px-2 py-0.5 rounded-full border ${i === curIdx ? "bg-primary text-white border-primary" : i < curIdx ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>{s}</span>
          ))}
        </div>
        <Textarea placeholder="Catatan progres (opsional)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="stage-note" className="mt-1" />
        <div className="flex flex-wrap gap-2">
          {nextStage && canAdvanceTo(nextStage) && (
            <Button className="bg-primary hover:bg-red-700 h-11 text-base sm:h-9 sm:text-sm" data-testid="stage-advance-btn" onClick={advance}>
              Lanjut ke {nextStage} <ArrowRight size={16} className="ml-1.5" />
            </Button>
          )}
          {!nextStage && <span className="text-xs text-emerald-700 font-medium self-center">Tahap akhir (Closed) tercapai.</span>}
          {user && CAN_STAGE_BACK.includes(user.role) && curIdx > 0 && (
            <Select onValueChange={goBackStage}>
              <SelectTrigger className="w-[190px] h-11 sm:h-9" data-testid="stage-back-select"><SelectValue placeholder="Mundur tahap (alasan)" /></SelectTrigger>
              <SelectContent>{flow.slice(0, curIdx).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          )}
        </div>
      </Card>

      <Tabs defaultValue="overview">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Ringkasan</TabsTrigger>
          {canViewDeal && <TabsTrigger value="deal" data-testid="tab-deal">Harga Deal</TabsTrigger>}
          {canViewEst && <TabsTrigger value="penawaran" data-testid="tab-penawaran">Penawaran</TabsTrigger>}
          {canViewRab && <TabsTrigger value="rab" data-testid="tab-rab">RAB Bahan</TabsTrigger>}
          {canViewRap && <TabsTrigger value="rap" data-testid="tab-rap">RAP</TabsTrigger>}
          <TabsTrigger value="timeline" data-testid="tab-timeline">Produksi</TabsTrigger>
          {canViewHpp && <TabsTrigger value="hpp" data-testid="tab-hpp">HPP</TabsTrigger>}
          <TabsTrigger value="photos" data-testid="tab-photos">Dokumen</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Audit</TabsTrigger>
        </TabsList>

        {canViewRap && <TabsContent value="rap" className="mt-4"><RapPanel projectId={id} /></TabsContent>}
        {canViewDeal && (
          <TabsContent value="deal" className="mt-4"><DealCard projectId={id} onChanged={load} /></TabsContent>
        )}
        {canViewEst && (
          <TabsContent value="penawaran" className="mt-4">
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold text-navy text-sm">Penawaran Proyek Ini</h3>
                {user && ESTIMATE_MANAGE.includes(user.role) && <Button size="sm" className="bg-primary hover:bg-red-700" data-testid="proj-new-estimate" onClick={() => navigate(`/estimates/new?project=${id}`)}>Buat Penawaran</Button>}
              </div>
              {estList.length === 0 ? <p className="text-sm text-muted-foreground">Belum ada penawaran.</p> : (
                <div className="space-y-2">{estList.map((e) => (
                  <div key={e.id} className="flex items-center justify-between border rounded-md p-2.5 cursor-pointer hover:border-primary" onClick={() => navigate(`/estimates/${e.id}`)} data-testid={`proj-est-${e.estimate_no}`}>
                    <div><div className="font-mono-k3 text-xs text-primary">{e.estimate_no}</div><div className="text-xs text-muted-foreground">{e.status} · {fmtDate(e.date)}</div></div>
                    <ArrowRight size={14} className="text-muted-foreground" />
                  </div>))}
                </div>
              )}
            </Card>
          </TabsContent>
        )}
        {canViewRab && (
          <TabsContent value="rab" className="mt-4">
            <Card className="p-4">
              <h3 className="font-heading font-semibold text-navy text-sm mb-3">RAB Bahan Proyek Ini</h3>
              {rabList.length === 0 ? <p className="text-sm text-muted-foreground">Belum ada RAB.</p> : (
                <div className="space-y-2">{rabList.map((r) => (
                  <div key={r.id} className="flex items-center justify-between border rounded-md p-2.5 cursor-pointer hover:border-primary" onClick={() => navigate(`/rab/${r.id}`)} data-testid={`proj-rab-${r.rab_no}`}>
                    <div><div className="font-mono-k3 text-xs text-primary">{r.rab_no}</div><div className="text-xs text-muted-foreground">{r.items?.length || 0} item bahan</div></div>
                    <ArrowRight size={14} className="text-muted-foreground" />
                  </div>))}
                </div>
              )}
            </Card>
          </TabsContent>
        )}
        {canViewHpp && (
          <TabsContent value="hpp" className="mt-4 space-y-3">
            <Card className="p-4 flex items-center justify-between">
              <div><h3 className="font-heading font-semibold text-navy text-sm">HPP Kendaraan (Material Sheet)</h3><p className="text-xs text-muted-foreground">Akumulasi pemakaian bahan aktual. RAB hanya rencana anggaran.</p></div>
              <Button size="sm" variant="outline" data-testid="proj-open-hpp" onClick={() => navigate(`/hpp/${id}/${p.vehicle_id}`)}>Buka Detail HPP</Button>
            </Card>
            {hppSum && canViewProfit && (
              <Card className="p-4">
                <h3 className="font-heading font-semibold text-navy text-sm mb-3">Kontrol Biaya Proyek</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">Nilai Penawaran</div><div className="font-bold text-navy" data-testid="proj-hpp-offer">{fmtOrEmpty(hppSum.offer_value_net)}</div></div>
                  <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">RAP (Rencana)</div><div className="font-bold text-navy" data-testid="proj-hpp-rab">{fmtOrEmpty(hppSum.rap_plan)}</div></div>
                  <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">HPP Aktual</div><div className="font-bold text-navy" data-testid="proj-hpp-actual">{fmtOrEmpty(hppSum.hpp_actual)}</div></div>
                  <div className={`rounded-md border p-3 ${hppSum.budget_status === "Over Budget" ? "border-red-300 bg-red-50" : hppSum.budget_status === "Saving" ? "border-emerald-300 bg-emerald-50" : ""}`}>
                    <div className="text-xs text-muted-foreground">Selisih RAP − HPP</div>
                    <div className={`font-bold ${hppSum.variance == null ? "text-slate-500 text-sm" : hppSum.variance < 0 ? "text-red-600" : "text-emerald-700"}`} data-testid="proj-hpp-variance">{fmtOrEmpty(hppSum.variance)}</div>
                    <div className={`text-[10px] mt-0.5 font-semibold ${hppSum.budget_status === "Over Budget" ? "text-red-600" : "text-emerald-700"}`} data-testid="proj-hpp-budget">{hppSum.budget_status}</div>
                  </div>
                </div>
                {hppSum.budget_status === "Over Budget" && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="proj-hpp-overwarn">
                    <AlertTriangle size={14} /> HPP aktual melebihi RAB. Biaya produksi over budget.
                  </div>
                )}
              </Card>
            )}
          </TabsContent>
        )}

        <TabsContent value="overview" className="grid md:grid-cols-2 gap-4 mt-4">
          {canViewProfit && hppSum && <div className="md:col-span-2"><FinancialSummary h={hppSum} /></div>}
          <Card className="p-4"><h3 className="font-heading font-semibold text-navy text-sm mb-2">Identitas Proyek</h3>
            <Row label="Project ID" value={<span className="font-mono-k3 text-primary">{p.project_id}</span>} />
            {p.ro_number && <Row label="Repair Order" value={<span className="font-mono-k3 text-emerald-700">{p.ro_number}</span>} />}
            <Row label="Tahap" value={<StatusBadge status={p.stage} />} />
            <Row label="Status Harga Deal" value={<span data-testid="deal-status-label" className={`text-xs font-semibold ${p.deal_status === "Terkunci" ? "text-emerald-700" : "text-amber-700"}`}>{p.deal_status === "Terkunci" ? "DEAL TERKUNCI" : "BELUM DEAL"}</span>} />
            <Row label="Jenis Pekerjaan" value={p.work_type} />
            <Row label="Prioritas" value={p.priority} />
            <Row label="Penanggung Jawab" value={p.penanggung_jawab_name || <span className="text-primary">Belum ada penanggung jawab</span>} />
            <Row label="Tanggal Masuk" value={p.unit_in_at ? fmtDateTime(p.unit_in_at) : fmtDate(p.plan_in_date)} />
            <Row label="Target Selesai" value={fmtDate(p.target_date)} />
            {p.days_running != null && <Row label="Hari Berjalan" value={`${p.days_running} hari`} />}
            <Row label="Lokasi Unit" value={p.location} />
          </Card>
          <Card className="p-4"><h3 className="font-heading font-semibold text-navy text-sm mb-2">Data Customer</h3>
            <Row label="Customer" value={p.customer_name} />
            <Row label="PIC" value={p.pic_customer} />
            <Row label="Telepon PIC" value={p.pic_phone} />
            <h3 className="font-heading font-semibold text-navy text-sm mb-2 mt-4">Data Kendaraan</h3>
            <Row label="No. Polisi" value={p.plate} />
            <Row label="Merek / Tipe" value={`${p.vehicle?.brand || ""} ${p.vehicle?.model || ""}`.trim()} />
            <Row label="Tahun" value={p.vehicle?.year} />
            <Row label="Jenis" value={p.vehicle?.vehicle_type} />
            <Row label="No. Rangka" value={p.vehicle?.chassis_number} />
            <Row label="No. Mesin" value={p.vehicle?.engine_number} />
          </Card>
          <Card className="p-4 md:col-span-2"><h3 className="font-heading font-semibold text-navy text-sm mb-2">Deskripsi & Catatan Customer</h3>
            <p className="text-sm text-slate-600">{p.description || "-"}</p>
            {p.notes && <p className="text-sm text-slate-500 mt-2 border-t pt-2">Catatan khusus: {p.notes}</p>}
          </Card>
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <Card className="p-4">
            <h3 className="font-heading font-semibold text-navy text-sm mb-3 flex items-center gap-2"><Clock size={16} /> Riwayat Tahap</h3>
            <div className="space-y-3">
              {(p.stage_history || []).slice().reverse().map((h, i) => (
                <div key={i} className="flex gap-3 text-sm" data-testid={`stage-hist-${i}`}>
                  <div className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
                  <div><div className="font-medium">{h.stage}</div>
                    <div className="text-xs text-muted-foreground">{fmtDateTime(h.at)} · {h.by}{h.note ? ` · ${h.note}` : ""}{h.reason ? ` · Alasan: ${h.reason}` : ""}</div></div>
                </div>
              ))}
              {(!p.stage_history || p.stage_history.length === 0) && <p className="text-sm text-muted-foreground">Belum ada riwayat tahap.</p>}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="photos" className="mt-4 space-y-3">
          {canEdit && (
            <Card className="p-3 flex items-center gap-2 flex-wrap">
              <Select value={photoCat} onValueChange={setPhotoCat}>
                <SelectTrigger className="w-[180px]" data-testid="detail-photo-category"><SelectValue /></SelectTrigger>
                <SelectContent>{PHOTO_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <label className="inline-flex items-center gap-2 px-3 py-2 border rounded-md text-sm cursor-pointer hover:bg-slate-50" data-testid="detail-photo-add">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />} Tambah Foto
                <input type="file" accept="image/*" capture="environment" multiple className="hidden" data-testid="detail-photo-input" onChange={(e) => uploadPhoto(e.target.files)} />
              </label>
            </Card>
          )}
          {(!p.photos || p.photos.length === 0) ? (
            <Card className="p-10 text-center text-muted-foreground">Belum ada foto.</Card>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {p.photos.map((ph) => (
                <div key={ph.id} className="relative group" data-testid={`photo-${ph.id}`}>
                  <img src={imgUrl(ph.storage_path)} alt={ph.category} onClick={() => setLightbox(ph)} className="w-full h-32 object-cover rounded-lg border cursor-zoom-in" />
                  <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] px-1.5 py-1 rounded-b-lg">{ph.category}</span>
                  {canEdit && (
                    <button onClick={() => deletePhoto(ph.id)} data-testid={`delete-photo-${ph.id}`}
                      className="absolute top-1.5 right-1.5 bg-primary text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={12} /></button>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50"><tr className="text-left text-xs text-muted-foreground">
                <th className="py-2 px-3">Waktu</th><th className="px-3">User</th><th className="px-3">Tindakan</th><th className="px-3 hidden sm:table-cell">Perubahan</th>
              </tr></thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-t" data-testid={`history-row-${h.id}`}>
                    <td className="py-2 px-3 text-xs">{fmtDateTime(h.timestamp)}</td>
                    <td className="px-3 text-xs">{h.user_name} <span className="text-muted-foreground">({h.role})</span></td>
                    <td className="px-3 text-xs font-medium">{h.action}{h.reason && <div className="text-[10px] text-primary">Alasan: {h.reason}</div>}</td>
                    <td className="px-3 text-xs hidden sm:table-cell text-muted-foreground">
                      {h.before && <div>Dari: {JSON.stringify(h.before)}</div>}
                      {h.after && <div>Ke: {JSON.stringify(h.after)}</div>}
                    </td>
                  </tr>
                ))}
                {history.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-muted-foreground">Belum ada riwayat.</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-w-3xl p-2">
          {lightbox && <><img src={imgUrl(lightbox.storage_path)} alt="" className="w-full rounded-md" />
            <div className="text-xs text-muted-foreground px-2 pb-1">{lightbox.category} · diunggah {fmtDateTime(lightbox.uploaded_at)} oleh {lightbox.uploaded_by}</div></>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
